import React, { useEffect, useState } from "react";
import BreadCrumb from "components/common/BreadCrumb";
import { BackButton } from "components/common/Buttons";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  Col,
  Row,
  Input,
  Label,
  Form,
  Card,
  CardBody,
  CardFooter,
  Container,
  CardHeader,
  FormFeedback,
  InputGroup,
  Button,
  Alert,
} from "reactstrap";
import { useFormik } from "formik";
import Flatpickr from "react-flatpickr";
import moment from "moment";
import schema from "./schema";

import {
  createEmployeeAdvance,
  getEmployeeAdvance,
  updateEmployeeAdvance,
} from "features/EmployeeAdvances/services/EmployeeAdvanceService";
import { EmployeeAdvance } from "features/EmployeeAdvances/types/employee-advance.types";
import {
  EmployeeAdvanceStatus,
  EmployeeAdvanceValueType,
  EmployeeAdvanceValueTypeLabel,
} from "features/EmployeeAdvances/constants/employee-advance.constants";
import { AsyncPaginatedSelect } from "components/common/AsyncPaginatedSelect";
import { getEmployeeOptions } from "features/Employees/services/EmployeeService";
import {
  EmployeeOption,
  EmployeeSingleValue,
} from "features/Employees/components/EmployeeSelectComponents";
import { getModeOfPaymentOptions } from "features/Meta/services/MetaService";
import { toast } from "react-toastify";
import { getDocumentTitle } from "utils/helpers";
import { formatCurrency } from "utils/currency";
import { listEmployeeSalaryStructures } from "features/Employees/services/employeeSalaryStructureService";
import type { EmployeeSalaryStructure } from "features/Employees/types/employee-salary-structure.types";

function resolveCurrentSalaryStructure(
  structures: EmployeeSalaryStructure[],
): EmployeeSalaryStructure | null {
  const today = moment().startOf("day");
  const current = structures.find((s) => {
    const from = moment(s.effectiveFrom);
    const to = s.effectiveTo ? moment(s.effectiveTo) : null;
    return (
      from.isSameOrBefore(today, "day") &&
      (!to || to.isSameOrAfter(today, "day"))
    );
  });
  return current ?? null;
}

function employeeDefaultOption(record: EmployeeAdvance | null) {
  const src = record?.employee;
  if (!src) return undefined;
  const fn = src.firstName ?? src.user?.firstName ?? "";
  const ln = src.lastName ?? src.user?.lastName ?? "";
  const name = [fn, ln].filter(Boolean).join(" ").trim();
  const label = name || src.code?.trim() || `Employee #${src.id}`;
  return {
    value: src.id,
    label,
    code: src.code?.trim() || undefined,
    firstName: fn || undefined,
    lastName: ln || undefined,
    avatar: src.avatar ?? src.user?.avatar ?? undefined,
  };
}

const EmployeeAdvanceForm = ({ isEdit = false }: { isEdit?: boolean }) => {
  document.title = getDocumentTitle(isEdit ? "Edit Employee Advance" : "New Employee Advance");

  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const preselectedEmployeeId = searchParams.get("employeeId");

  const [record, setRecord] = useState<EmployeeAdvance | null>(null);
  const [employeeSalary, setEmployeeSalary] =
    useState<EmployeeSalaryStructure | null>(null);
  const [employeeSalaryLoading, setEmployeeSalaryLoading] = useState(false);

  const isLocked = !!record && record.status !== EmployeeAdvanceStatus.PROCESSED;

  const lockedFormAlertMessage =
    record == null
      ? null
      : record.status === EmployeeAdvanceStatus.CANCELLED
        ? "This advance has been cancelled and can no longer be edited."
        : record.status === EmployeeAdvanceStatus.SETTLED
          ? "This advance is fully settled and can no longer be edited."
          : record.status === EmployeeAdvanceStatus.REFUNDED
            ? "This advance has been refunded and can no longer be edited."
            : record.status === EmployeeAdvanceStatus.DRAFT
              ? "This advance has not been disbursed yet — use \"Disburse\" on the advance before editing."
              : null;

  const validation: any = useFormik({
    enableReinitialize: true,
    initialValues: {
      employeeId: record?.employeeId ?? preselectedEmployeeId ?? "",
      valueType: record?.valueType ?? EmployeeAdvanceValueType.FIXED,
      value:
        record == null
          ? ""
          : (record.valueType ?? EmployeeAdvanceValueType.FIXED) ===
              EmployeeAdvanceValueType.PERCENTAGE
            ? (record.percentage ?? "")
            : (record.amount ?? ""),
      remarks: record?.remarks ?? "",
      purpose: record?.purpose ?? "",
      isEdit,
      markAsPaid: true,
      modeOfPaymentId: "",
      paymentDate: "",
      referenceNumber: "",
    },
    validationSchema: schema,
    onSubmit: async (values) => {
      if (record && isLocked) {
        toast.error("This advance can no longer be edited.");
        return;
      }
      const n = Number(values.value);
      const valueType = values.valueType as EmployeeAdvanceValueType;
      if (!isEdit) {
        if (!Number.isFinite(n) || n <= 0) {
          toast.error("Enter a valid amount or percentage.");
          return;
        }
        const resolvedAmount =
          valueType === EmployeeAdvanceValueType.PERCENTAGE
            ? employeeSalary?.netAmount != null
              ? (employeeSalary.netAmount * n) / 100
              : null
            : n;
        if (
          resolvedAmount != null &&
          employeeSalary?.netAmount != null &&
          resolvedAmount > employeeSalary.netAmount
        ) {
          toast.error(
            `Advance amount (${formatCurrency(resolvedAmount)}) cannot exceed the employee's net salary (${formatCurrency(employeeSalary.netAmount)}).`,
          );
          return;
        }
      }
      const remarks =
        values.remarks === "" || values.remarks == null ? null : String(values.remarks);
      const purpose =
        values.purpose === "" || values.purpose == null ? null : String(values.purpose);

      let response;
      try {
        if (isEdit) {
          response = await updateEmployeeAdvance({
            id: Number(id),
            remarks,
            purpose,
          });
        } else {
          const markAsPaid = values.markAsPaid !== false;
          const amountField =
            valueType === EmployeeAdvanceValueType.FIXED
              ? { amount: n }
              : { percent: n };
          response = await createEmployeeAdvance({
            employeeId: Number(values.employeeId),
            ...amountField,
            remarks,
            purpose,
            markAsPaid,
            ...(markAsPaid
              ? {
                  modeOfPaymentId:
                    values.modeOfPaymentId === "" || values.modeOfPaymentId == null
                      ? null
                      : Number(values.modeOfPaymentId),
                  paymentDate: values.paymentDate || null,
                  referenceNumber: values.referenceNumber?.trim() || null,
                }
              : {}),
          });
        }
        if (response.success) {
          toast.success(response.message);
          navigate(-1);
        }
      } catch (err) {
        toast.error((err as any).response?.data?.message ?? "Something went wrong.");
      }
    },
  });

  useEffect(() => {
    if (!id) return;

    const fetchRecord = async () => {
      try {
        const response = await getEmployeeAdvance(Number(id));
        if (response.success) {
          setRecord(response.data);
        }
      } catch (err) {
        console.warn(err);
      }
    };

    fetchRecord();
  }, [id]);

  const selectedEmployeeId = validation.values.employeeId;

  useEffect(() => {
    const employeeId = Number(selectedEmployeeId);
    if (!Number.isFinite(employeeId) || employeeId <= 0) {
      setEmployeeSalary(null);
      setEmployeeSalaryLoading(false);
      return;
    }

    let cancelled = false;
    setEmployeeSalaryLoading(true);
    void (async () => {
      try {
        const response = await listEmployeeSalaryStructures(employeeId);
        if (!cancelled && response.success) {
          setEmployeeSalary(resolveCurrentSalaryStructure(response.data ?? []));
        }
      } catch (err) {
        if (!cancelled) {
          console.warn(err);
          setEmployeeSalary(null);
        }
      } finally {
        if (!cancelled) setEmployeeSalaryLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [selectedEmployeeId]);

  const resolvedAdvanceAmount =
    validation.values.value !== "" &&
    !Number.isNaN(Number(validation.values.value))
      ? validation.values.valueType === EmployeeAdvanceValueType.PERCENTAGE
        ? employeeSalary?.netAmount != null
          ? (employeeSalary.netAmount * Number(validation.values.value)) / 100
          : null
        : Number(validation.values.value)
      : null;
  const advanceExceedsNetSalary =
    resolvedAdvanceAmount != null &&
    employeeSalary?.netAmount != null &&
    resolvedAdvanceAmount > employeeSalary.netAmount;

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <BreadCrumb
            title={isEdit ? "Edit Employee Advance" : "New Employee Advance"}
            pageTitle="Finance"
            items={[{ label: "Employee Advances", path: "/finance/employee-advances" }]}
          />
          <Form
            className="needs-validation"
            onSubmit={(e) => {
              e.preventDefault();
              validation.handleSubmit();
              return false;
            }}
          >
            <Row className="justify-content-center">
              <Col xl={12} lg={10}>
                <Card className="shadow-none border">
                  <CardHeader className="border-bottom bg-transparent pb-3">
                    <h5 className="card-title mb-1">
                      {isEdit ? "Edit employee advance" : "Create employee advance"}
                    </h5>
                    <p className="text-muted mb-0 small">
                      Salary advance disbursed to an employee. By default the
                      advance is disbursed immediately — uncheck "Mark as
                      received" below to log it before the money has actually
                      gone out.
                    </p>
                  </CardHeader>
                  <CardBody className="pt-4">
                    {lockedFormAlertMessage ? (
                      <Alert color="warning" className="mb-4" role="alert">
                        <i className="ri-information-line me-2 align-middle" />
                        {lockedFormAlertMessage}
                      </Alert>
                    ) : null}

                    <div className="text-uppercase text-muted fw-semibold fs-12 mb-3">
                      Employee
                    </div>
                    <Row className="g-3 mb-2">
                      <Col md={6}>
                        <Label className="form-label">Employee</Label>
                        {isEdit ? (
                          <Input
                            type="text"
                            className="form-control"
                            value={employeeDefaultOption(record)?.label ?? `#${record?.employeeId ?? ""}`}
                            readOnly
                            disabled
                          />
                        ) : (
                          <>
                            <AsyncPaginatedSelect
                              components={{
                                Option: EmployeeOption,
                                SingleValue: EmployeeSingleValue,
                              }}
                              fetchOptions={getEmployeeOptions}
                              placeholder="Search employees…"
                              value={validation.values.employeeId}
                              onChange={(val: any) =>
                                validation.setFieldValue("employeeId", val?.value)
                              }
                              onBlur={() => validation.setFieldTouched("employeeId", true)}
                              isInvalid={
                                validation.touched.employeeId && !!validation.errors.employeeId
                              }
                              isDisabled={!!preselectedEmployeeId}
                            />
                            {validation.errors.employeeId &&
                              (validation.touched.employeeId || validation.submitCount > 0) && (
                                <FormFeedback type="invalid" className="d-block">
                                  {validation.errors.employeeId}
                                </FormFeedback>
                              )}
                          </>
                        )}
                        {selectedEmployeeId && !employeeSalaryLoading ? (
                          <small className="text-muted d-block mt-2">
                            Net salary:{" "}
                            {employeeSalary ? (
                              <span className="text-body fw-medium">
                                {formatCurrency(employeeSalary.netAmount)}
                              </span>
                            ) : (
                              "— no current salary structure on file"
                            )}
                          </small>
                        ) : null}
                      </Col>
                    </Row>

                    <hr className="my-4 text-muted opacity-25" />

                    <div className="text-uppercase text-muted fw-semibold fs-12 mb-3">
                      Value
                    </div>
                    <Row className="mb-2">
                      <Col lg={6}>
                        <Label className="form-label" for="employee-advance-value">
                          {validation.values.valueType ===
                          EmployeeAdvanceValueType.PERCENTAGE
                            ? "Percentage"
                            : "Amount (INR)"}
                        </Label>
                        <InputGroup>
                          <Input
                            id="employee-advance-value"
                            type="number"
                            className="form-control"
                            placeholder={
                              validation.values.valueType ===
                              EmployeeAdvanceValueType.PERCENTAGE
                                ? "e.g. 10"
                                : "0.00"
                            }
                            name="value"
                            step="0.01"
                            readOnly={isEdit}
                            value={validation.values.value}
                            onChange={validation.handleChange}
                            onBlur={validation.handleBlur}
                            invalid={Boolean(
                              (validation.errors.value &&
                                (validation.touched.value ||
                                  validation.submitCount > 0)) ||
                                advanceExceedsNetSalary,
                            )}
                          />
                          <Input
                            type="select"
                            className="form-select"
                            style={{ maxWidth: "11rem" }}
                            name="valueType"
                            value={validation.values.valueType}
                            onChange={(e) => {
                              validation.setFieldValue(
                                "valueType",
                                e.target.value as EmployeeAdvanceValueType,
                              );
                              validation.setFieldTouched("value", true);
                            }}
                            onBlur={validation.handleBlur}
                            disabled={isEdit}
                          >
                            {Object.values(EmployeeAdvanceValueType).map((vt) => (
                              <option key={vt} value={vt}>
                                {EmployeeAdvanceValueTypeLabel[vt]}
                              </option>
                            ))}
                          </Input>
                        </InputGroup>
                        <small className="text-muted d-block mt-2">
                          Fixed is a rupee amount. Percentage is resolved
                          against the employee's net salary
                          {employeeSalary ? (
                            <>
                              {" "}
                              of{" "}
                              <span className="fw-medium">
                                {formatCurrency(employeeSalary.netAmount)}
                              </span>
                            </>
                          ) : (
                            "."
                          )}
                        </small>
                        {validation.values.valueType ===
                          EmployeeAdvanceValueType.PERCENTAGE &&
                        resolvedAdvanceAmount != null ? (
                          <small className="text-muted d-block mt-1">
                            Preview:{" "}
                            <span className="text-body fw-medium">
                              {formatCurrency(resolvedAdvanceAmount)}
                            </span>
                          </small>
                        ) : null}
                        {isEdit ? (
                          <small className="text-muted d-block mt-1">
                            Amount cannot be changed after creation.
                          </small>
                        ) : null}
                        {validation.errors.value &&
                          (validation.touched.value || validation.submitCount > 0) && (
                            <FormFeedback type="invalid" className="d-block">
                              {validation.errors.value}
                            </FormFeedback>
                          )}
                        {advanceExceedsNetSalary ? (
                          <FormFeedback type="invalid" className="d-block">
                            Advance amount (
                            {formatCurrency(resolvedAdvanceAmount ?? 0)}) cannot
                            exceed the employee's net salary (
                            {formatCurrency(employeeSalary?.netAmount ?? 0)}).
                          </FormFeedback>
                        ) : null}
                      </Col>
                    </Row>

                    {!isEdit ? (
                      <>
                        <hr className="my-4 text-muted opacity-25" />
                        <div className="text-uppercase text-muted fw-semibold fs-12 mb-3">
                          Disbursement payment
                        </div>
                        <div className="form-check mb-3">
                          <Input
                            type="checkbox"
                            className="form-check-input"
                            id="employee-advance-mark-as-paid"
                            checked={validation.values.markAsPaid}
                            onChange={(e) =>
                              validation.setFieldValue("markAsPaid", e.target.checked)
                            }
                          />
                          <Label
                            className="form-check-label"
                            for="employee-advance-mark-as-paid"
                          >
                            Mark as paid
                          </Label>
                          <p className="text-muted small mb-0">
                            {validation.values.markAsPaid
                              ? "The payment is created immediately along with the advance."
                              : "Recorded as Awaiting Disbursement — no payment yet. Use \"Disburse\" on the advance once the money goes out."}
                          </p>
                        </div>
                        {validation.values.markAsPaid ? (
                          <Row className="g-3 mb-2">
                            <Col md={6}>
                              <Label className="form-label">
                                Mode of payment <span className="text-danger">*</span>
                              </Label>
                              <AsyncPaginatedSelect
                                fetchOptions={getModeOfPaymentOptions}
                                placeholder="Search modes…"
                                value={validation.values.modeOfPaymentId}
                                onChange={(val: any) =>
                                  validation.setFieldValue("modeOfPaymentId", val?.value ?? "")
                                }
                                onBlur={() =>
                                  validation.setFieldTouched("modeOfPaymentId", true)
                                }
                                isInvalid={
                                  (validation.touched.modeOfPaymentId ||
                                    validation.submitCount > 0) &&
                                  !!validation.errors.modeOfPaymentId
                                }
                              />
                              {(validation.touched.modeOfPaymentId ||
                                validation.submitCount > 0) &&
                                validation.errors.modeOfPaymentId && (
                                  <FormFeedback type="invalid" className="d-block">
                                    {validation.errors.modeOfPaymentId}
                                  </FormFeedback>
                                )}
                            </Col>
                            <Col md={6}>
                              <Label className="form-label" for="employee-advance-payment-date">
                                Payment date <span className="text-muted fw-normal">(optional)</span>
                              </Label>
                              <Flatpickr
                                id="employee-advance-payment-date"
                                className="form-control"
                                placeholder="Select date"
                                options={{ altInput: true, altFormat: "d M, Y", dateFormat: "d M, Y" }}
                                onChange={(date: any) =>
                                  validation.setFieldValue(
                                    "paymentDate",
                                    date[0] ? moment(date[0]).format("YYYY-MM-DD") : "",
                                  )
                                }
                                value={
                                  validation.values.paymentDate
                                    ? new Date(validation.values.paymentDate)
                                    : ""
                                }
                              />
                            </Col>
                            <Col md={6}>
                              <Label className="form-label" for="employee-advance-reference-number">
                                Reference number{" "}
                                <span className="text-muted fw-normal">(optional)</span>
                              </Label>
                              <Input
                                id="employee-advance-reference-number"
                                type="text"
                                className="form-control"
                                placeholder="UTR, cheque no., transaction id…"
                                name="referenceNumber"
                                value={validation.values.referenceNumber}
                                onChange={validation.handleChange}
                                onBlur={validation.handleBlur}
                              />
                            </Col>
                          </Row>
                        ) : null}
                      </>
                    ) : null}

                    <hr className="my-4 text-muted opacity-25" />

                    <div className="text-uppercase text-muted fw-semibold fs-12 mb-3">
                      Purpose
                    </div>
                    <Row className="mb-4">
                      <Col>
                        <Label className="form-label" for="employee-advance-purpose">
                          Purpose <span className="text-muted fw-normal">(optional)</span>
                        </Label>
                        <Input
                          id="employee-advance-purpose"
                          type="textarea"
                          className="form-control"
                          rows={2}
                          placeholder="Why this advance is being raised (e.g. medical, travel)…"
                          name="purpose"
                          value={validation.values.purpose}
                          onChange={validation.handleChange}
                          onBlur={validation.handleBlur}
                          disabled={isLocked}
                        />
                      </Col>
                    </Row>

                    <div className="text-uppercase text-muted fw-semibold fs-12 mb-3">
                      Remarks
                    </div>
                    <Row>
                      <Col>
                        <Label className="form-label visually-hidden" for="employee-advance-remarks">
                          Remarks
                        </Label>
                        <Input
                          id="employee-advance-remarks"
                          type="textarea"
                          className="form-control"
                          rows={3}
                          placeholder="Optional notes for HR or finance…"
                          name="remarks"
                          value={validation.values.remarks}
                          onChange={validation.handleChange}
                          onBlur={validation.handleBlur}
                          disabled={isLocked}
                        />
                      </Col>
                    </Row>
                  </CardBody>
                  <CardFooter className="bg-light bg-opacity-50 border-top d-flex flex-wrap justify-content-between align-items-center gap-2 py-3">
                    <BackButton />
                    <Button
                      type="submit"
                      color="primary"
                      className="btn-label px-4"
                      disabled={
                        isLocked ||
                        validation.isSubmitting ||
                        (!isEdit && advanceExceedsNetSalary)
                      }
                    >
                      {validation.isSubmitting ? (
                        <>
                          <span
                            className="spinner-border spinner-border-sm me-2"
                            role="status"
                            aria-hidden="true"
                          />
                          {isEdit ? "Updating…" : "Submitting…"}
                        </>
                      ) : isEdit ? (
                        "Save changes"
                      ) : (
                        "Create advance"
                      )}
                    </Button>
                  </CardFooter>
                </Card>
              </Col>
            </Row>
          </Form>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default EmployeeAdvanceForm;
